import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { BeatLoader } from "react-spinners";
import tailwindClassnames from "../common/TailwindClassnames";
import { isTopLayerOpen } from "../common/topLayer";
import SpeciesPicker from "./SpeciesPicker";

export type PointToSpeciesFields = {
  taxonId: number;
  comments: string | null;
  setReviewed: boolean;
};

type PointToSpeciesModalProps = {
  /** Project scope for the species picker. */
  projectId: number;
  /** How many photos the action will be applied to — the count the bar shows. */
  selectedCount: number;
  /** True while the commit is in flight; makes every control inert. */
  busy?: boolean;
  /** Called with the confirmed fields (taxon guaranteed non-null). */
  onConfirm: (fields: PointToSpeciesFields) => void;
  /** Called when the modal is dismissed without confirming. */
  onCancel: () => void;
};

/**
 * The Point-to-one-species confirmation (AKL-811).
 *
 * **Its own component, not `ObservationEditor` and not a fourth
 * `ConfirmationModal` caller.** The two modals differ in more than fields — no
 * count, a five-bullet explanation, a destructive framing — and reaching this
 * feature through the editor would mean an optional prop and a conditional
 * around the count, with the approved copy living behind an `if`.
 *
 * **Single stage.** The bullets say what will and will not change, so this modal
 * *is* the confirmation; a second dialog would ask the reviewer to confirm the
 * same sentence twice. There is no undo, which is why the modal is the entire
 * safety net.
 *
 * **The copy is approved verbatim (Margaux, 2026-09-10)** — title, lead, all
 * five bullets, the checkbox label and the confirm label. Any change needs fresh
 * sign-off; paraphrasing one is a defect, not a style choice.
 *
 * **There is no count field, deliberately.** A count is a statement about one
 * photo, and one number written across an arbitrary selection would assert
 * something the reviewer never looked at. The server derives the count per photo
 * from the number of distinct bounding boxes the re-pointed predictions cover.
 *
 * State is local and short-lived: the page renders this only while the flow is
 * open, so every open starts from the defaults by construction — including the
 * reviewed checkbox, which starts ticked.
 *
 * **Dismissal is deliberately hard to trigger by accident**, for the same
 * reason: everything typed here is lost with no undo. Escape defers to an open
 * species menu, and the backdrop closes only on a press that both starts and
 * ends on it — see the two handlers below.
 */
const PointToSpeciesModal: React.FC<PointToSpeciesModalProps> = ({
  projectId,
  selectedCount,
  busy,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [taxonId, setTaxonId] = useState<number | null>(null);
  const [taxonLabel, setTaxonLabel] = useState<string | null>(null);
  const [comments, setComments] = useState<string>("");
  const [setReviewed, setSetReviewed] = useState<boolean>(true);
  // The panel, so Escape can ask whether a dropdown inside it is open.
  const panelRef = useRef<HTMLDivElement>(null);

  // Dismiss unless the commit is in flight — while busy the Cancel button is
  // disabled, so Escape / backdrop / × must be inert too, otherwise the modal
  // could close mid-commit and read as a cancelled action.
  const dismiss = () => {
    if (busy) return;
    onCancel();
  };

  // Escape cancels the modal. Handled here on the capture phase — not via the
  // page's global shortcut listener — so it works while the picker, the textarea
  // or the checkbox has focus, and `stopPropagation` keeps the grid's own
  // Escape (which would drop the selection underneath this modal) from ever
  // seeing it. The grid's `S` is gated by the page's `modalOpen` flag, which
  // carries this modal's open state for exactly that reason: a focused checkbox
  // does NOT count as "typing" in that handler, by design (AKL-753/784).
  //
  // **An open dropdown owns Escape first.** Closing the species menu is what a
  // reviewer means by that keypress, and tearing the whole modal down instead
  // would throw away the species, the comment and the checkbox with no undo
  // anywhere in this feature. So the menu's own state decides: while something
  // inside the panel is expanded, this listener stands aside entirely — no
  // cancel and no `stopPropagation`, so react-select's own handler closes the
  // menu — and the next Escape, with nothing expanded, closes the modal. That
  // is the same "close the inner dropdown first" precedence `ReviewPageFilter`
  // has; it reads the menu's `aria-expanded` rather than `defaultPrevented`
  // because react-select calls `preventDefault()` for Escape whether or not its
  // menu was open, which would leave Escape unable to close this modal at all
  // while the picker has focus.
  //
  // Standing aside lets the keypress reach the page's grid handler, which is
  // harmless: that handler bails while `pointToSpeciesOpen` is set.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // A dialog layered above this one owns Escape while it is open (AKL-798). Without this
      // guard, this capture-phase `window` listener runs before React's delegated handler on
      // that dialog's panel and cancels THIS modal — losing the staged species and comment —
      // so one Escape closed the dialog above and threw this one away with it.
      if (isTopLayerOpen()) return;
      if (e.key === "Escape") {
        if (panelRef.current?.querySelector('[aria-expanded="true"]')) return;
        e.stopPropagation();
        if (busy) return;
        onCancel();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onCancel, busy]);

  // Backdrop dismissal, tracked across the whole press rather than on the click
  // alone. A click event fires on the nearest common ancestor of where the
  // press started and where it ended, so a text-selection drag that begins in
  // the Comments box and releases on the backdrop produces a click whose target
  // IS the overlay — and dismissing on that tears the modal down, losing the
  // species and the comment, because the reviewer selected some text
  // imprecisely. Both ends of the press must land on the overlay itself.
  const pressStartedOnOverlay = useRef(false);
  const pressEndedOnOverlay = useRef(false);

  // Disabled until a species is chosen, rather than validating on submit:
  // nothing here can be partially valid. No `dirty` gate — unlike the editor
  // there is no no-op edit to guard against.
  const canConfirm = taxonId != null && !busy;

  const handleConfirm = () => {
    if (taxonId == null) return;
    onConfirm({
      taxonId,
      comments: comments.trim() === "" ? null : comments.trim(),
      setReviewed,
    });
  };

  return (
    <div
      className={`${tailwindClassnames.modalOverlay} z-[1200]`}
      onPointerDown={(e) => {
        pressStartedOnOverlay.current = e.target === e.currentTarget;
        pressEndedOnOverlay.current = false;
      }}
      onPointerUp={(e) => {
        pressEndedOnOverlay.current = e.target === e.currentTarget;
      }}
      onPointerCancel={() => {
        // The gesture became something else - a scroll, a gesture the OS took
        // over - so it was never a backdrop press. Cleared here because no
        // pointerup follows a cancel, and a stale `true` would make the NEXT
        // click dismiss the modal.
        pressStartedOnOverlay.current = false;
        pressEndedOnOverlay.current = false;
      }}
      onClick={() => {
        const backdropPress =
          pressStartedOnOverlay.current && pressEndedOnOverlay.current;
        pressStartedOnOverlay.current = false;
        pressEndedOnOverlay.current = false;
        if (!backdropPress) return;
        dismiss();
      }}
      data-testid="point-to-species-modal"
    >
      <div
        ref={panelRef}
        className={`${tailwindClassnames.modalPanel} max-w-md`}
      >
        <div className={tailwindClassnames.modalHeaderRow}>
          <div className="flex items-center gap-3">
            {/* Destructive framing — styling only, matching Mark as blank. The
                approved copy is untouched by it. */}
            <span
              aria-hidden="true"
              className={tailwindClassnames.modalDangerIcon}
            >
              !
            </span>
            <h2 className={tailwindClassnames.modalTitle}>
              {t("Point to one species")}
            </h2>
          </div>
          <button
            type="button"
            className={tailwindClassnames.modalCloseButton}
            onClick={dismiss}
            disabled={busy}
            aria-label={t("Close")}
            data-testid="point-to-species-close"
          >
            &times;
          </button>
        </div>

        <div className={`${tailwindClassnames.modalBody} flex flex-col gap-4`}>
          <div>
            <p data-testid="point-to-species-lead">
              {t("Record {{count}} selected photos as this species.", {
                count: selectedCount,
              })}
            </p>
            {/* The five bullets, in the spec's order. Each is its own approved
                string — do not merge, reorder or reword them. */}
            <ul className="mt-2 list-disc space-y-1 pl-5">
              <li>
                {t(
                  "All animals that were detected in these photos will point to this species.",
                )}
              </li>
              <li>
                {t(
                  "Photos where the AI found nothing will get a new observation of this species.",
                )}
              </li>
              <li>
                {t(
                  "Observations already saved by a reviewer are left as they are.",
                )}
              </li>
              <li>
                {t(
                  "People, vehicles and blanks the AI found are left as they are.",
                )}
              </li>
              <li>{t("This cannot be undone.")}</li>
            </ul>
          </div>

          <div>
            {/* No `seededOptions`: there is nothing to seed from — the
                selection's AI labels are the thing being overridden. `inputId`
                binds the picker's own "Species" label to its combobox, which is
                the only thing giving it an accessible name; without it the
                textarea and checkbox announce properly and the picker announces
                as a bare "combobox". */}
            <SpeciesPicker
              inputId="point-to-species-taxon"
              projectId={projectId}
              value={taxonId}
              valueLabel={taxonLabel}
              onChange={(id, label) => {
                setTaxonId(id);
                setTaxonLabel(label);
              }}
              disabled={busy}
            />
          </div>

          <div className="flex flex-col">
            <label
              className={tailwindClassnames.dropdownLabel}
              htmlFor="point-to-species-comments"
            >
              {t("Comments")}
            </label>
            <textarea
              id="point-to-species-comments"
              className={tailwindClassnames.detailsPageTextArea}
              rows={3}
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              disabled={busy}
              data-testid="point-to-species-comments"
            />
          </div>

          {/* NOT `ManageReviewPage`'s `renderSetReviewedCheckbox` helper: this
              label is different approved copy, and sharing the helper would
              silently reword the other two confirmations. */}
          <label className="flex items-center gap-2 text-sm text-off-black-light dark:text-primary-off-white">
            <input
              type="checkbox"
              className="h-4 w-4 accent-sky-blue-dark"
              checked={setReviewed}
              onChange={(e) => setSetReviewed(e.target.checked)}
              disabled={busy}
              data-testid="point-to-species-set-reviewed"
            />
            {t("Mark these photos as reviewed")}
          </label>
        </div>

        <div className={tailwindClassnames.modalFooter}>
          <button
            type="button"
            className={tailwindClassnames.btnSecondary}
            // Through `dismiss`, not `onCancel`, so every route out of this
            // modal enforces the busy guard in one place. `disabled={busy}`
            // already makes this button inert mid-commit, so this changes
            // nothing today - it means the guard cannot be lost if the disabled
            // state is ever refactored away.
            onClick={dismiss}
            disabled={busy}
            data-testid="point-to-species-cancel"
          >
            {t("Cancel")}
          </button>
          <button
            type="button"
            className={`relative ${tailwindClassnames.btnDanger}`}
            onClick={handleConfirm}
            disabled={!canConfirm}
            data-testid="point-to-species-confirm"
          >
            {/* Keep the label in layout (just hidden) while busy so the button
                row does not resize when the spinner replaces the text. */}
            <span className={busy ? "invisible" : undefined}>
              {t("Point to species")}
            </span>
            {busy && (
              <span className="absolute inset-0 flex items-center justify-center">
                <BeatLoader
                  color="currentColor"
                  loading={true}
                  size={10}
                  margin={5}
                />
              </span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PointToSpeciesModal;
